import React, { useState, useEffect } from 'react';
import { Zap, LayoutGrid, Calendar, Search, BookOpen, Scale, ChevronDown } from 'lucide-react';
import applaiLogo from '../assets/applai-logo.png';
import ContactModal from '../components/ContactModal';
import './LandingPage.css';

const NAV_LINKS = ['Features', 'How it Works', 'Pricing'];

const FEATURES = [
  { icon: <Zap size={20} />, title: 'AI Job Parsing', desc: 'Paste a URL or drop a PDF — AI fills everything in automatically.' },
  { icon: <LayoutGrid size={20} />, title: 'Kanban Board', desc: 'Drag post-its between status columns. Your pipeline at a glance.' },
  { icon: <Calendar size={20} />, title: 'Interview Calendar', desc: 'Schedule events with email reminders so nothing slips through.' },
  { icon: <Search size={20} />, title: 'AI Job Analysis', desc: 'Deep breakdown: score, compensation, red flags, what to do next.' },
  { icon: <BookOpen size={20} />, title: 'Interview Prep', desc: 'Review guide, flashcards, and a mock quiz — generated in seconds.' },
  { icon: <Scale size={20} />, title: 'AI Comparison', desc: '6-dimension scored comparison to pick the stronger offer.' },
];

const STEPS = [
  { num: '01', title: 'Add any job in seconds', desc: 'Paste a posting URL or drop a PDF. AI extracts the company, role, salary, and perks automatically — no typing required.', badge: 'Works with any job site' },
  { num: '02', title: 'See your whole pipeline', desc: 'A Kanban board groups every application by status. Drag a card to update it instantly. Switch to the table view for detailed sorting and filtering.', badge: 'Board and table views' },
  { num: '03', title: 'Know before you commit', desc: 'Run AI analysis on any application: fit score, compensation breakdown, red flags, and a clear "what to do next" — personalized to your profile.', badge: 'Powered by Gemini AI' },
  { num: '04', title: 'Walk in ready', desc: 'Generate a tailored review guide, flashcard deck, and mock quiz for each interview in seconds. Export as PDF and study anywhere.', badge: 'Tailored to you and the role' },
];

const PRO_BILLING = [
  {
    id: 'monthly',
    label: 'Monthly',
    totalPHP: 199, perMonthPHP: 199,
    totalUSD: '3.50', perMonthUSD: '3.50',
    badge: null, savingsPHP: null, savingsUSD: null,
    desc: 'Pay month-to-month, cancel anytime.',
  },
  {
    id: '3month',
    label: '3 Months',
    totalPHP: 549, perMonthPHP: 183,
    totalUSD: '9.65', perMonthUSD: '3.22',
    badge: 'Most Popular', savingsPHP: 48, savingsUSD: '0.84',
    desc: 'Save ₱48 vs monthly billing.',
  },
  {
    id: '6month',
    label: '6 Months',
    totalPHP: 999, perMonthPHP: 166,
    totalUSD: '17.57', perMonthUSD: '2.93',
    badge: 'Best Value', savingsPHP: 195, savingsUSD: '3.43',
    desc: 'Save ₱195 — lowest price per month.',
  },
];

const PRICING_ROWS = [
  { label: 'Credits / month',   starter: '15 credits',          pro: '300 credits' },
  { label: 'Job Tracker',       starter: true,                  pro: true },
  { label: 'Calendar',          starter: 'Basic only',          pro: 'Full + Email Reminders' },
  { label: 'Custom Event Types',starter: false,                 pro: true },
  { label: 'AI Job Parsing',    starter: 'Limited',             pro: 'URL + PDF Upload' },
  { label: 'AI Analysis',       starter: 'Limited',             pro: 'Full access' },
  { label: 'Interview Prep',    starter: 'Limited',             pro: 'Full access' },
  { label: 'Job Comparison',    starter: 'Basic only',          pro: 'Basic + AI Analysis' },
  { label: 'Resume Upload',     starter: false,                 pro: true },
  { label: 'Support',           starter: 'Standard',            pro: 'High Priority' },
];

const TESTIMONIALS = [
  { quote: 'I went from sending 40 applications with zero tracking to landing 3 final interviews in one month. The AI analysis showed me exactly what was wrong with how I was applying.', name: 'Norman S.', role: 'Software Engineer' },
  { quote: 'The flashcard generator is insane. I used it the night before my final interview and walked in more prepared than I\'ve ever been for anything in my career.', name: 'Angelo C.', role: 'Product Manager' },
  { quote: 'Finally a tool that actually helps instead of just tracking. The comparison feature made it so easy to decide between two offers.', name: 'Leila B.', role: 'UX Designer' },
];

const FAQS = [
  { q: 'Is ApplAI really free?', a: 'Yes — unlimited job tracking, the Kanban board, table view, and basic calendar are free forever. The free Starter plan includes 15 AI credits per month to try features like AI analysis and interview prep. Upgrade to Pro for 300 credits, email reminders, resume upload, and AI job comparison. Pro plans: ₱199/month, ₱549 for 3 months (~₱183/mo, save ₱48), or ₱999 for 6 months (~₱166/mo, save ₱195).' },
  { q: 'How does AI job parsing work?', a: 'Paste a job posting URL or upload a PDF. ApplAI fetches the content and sends it to Gemini AI, which extracts the company, role, location, salary, work setup, and perks into a structured form you can review and edit before saving.' },
  { q: 'Which job sites are supported?', a: 'Any publicly accessible job posting URL works — Jobstreet, Kalibrr, LinkedIn, Glassdoor, company career pages, and more. PDF uploads work with any job description saved as a file.' },
  { q: 'Is my data private?', a: 'Your applications, notes, and profile data are stored securely in a private database scoped to your account. We never sell your data or use it to train AI models.' },
  { q: 'Can I use ApplAI on mobile?', a: 'Yes, the interface is fully responsive. For the best experience on complex views like the Kanban board or analysis pages, a desktop browser is recommended.' },
  { q: 'What AI model powers ApplAI?', a: 'All AI features use Google Gemini — a fast, capable model optimized for structured extraction and analysis tasks.' },
];

const LandingPage = ({ onLogin }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [showContact, setShowContact] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [currency, setCurrency] = useState('PHP');

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="landing-root">
      {/* ── Navbar ── */}
      <nav className={`land-nav ${scrolled ? 'land-nav-scrolled' : ''}`}>
        <div className="land-nav-inner">
          <div className="land-nav-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={applaiLogo} alt="ApplAI" className="land-nav-logo" />
            <span className="land-nav-name">Appl<span className="land-ai">AI</span></span>
          </div>
          <div className="land-nav-links">
            {NAV_LINKS.map(l => <a key={l} href={`#${l.toLowerCase().replace(/\s+/g, '-')}`} className="land-nav-link">{l}</a>)}
          </div>
          <div className="land-nav-actions">
            <button className="land-btn-ghost" onClick={onLogin}>Sign In</button>
            <button className="land-btn-primary" onClick={onLogin}>Get Started Free</button>
          </div>
          <button className="land-mobile-menu-btn" onClick={() => setMobileMenuOpen(m => !m)}>
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="land-mobile-menu">
            {NAV_LINKS.map(l => <a key={l} href={`#${l.toLowerCase().replace(/\s+/g, '-')}`} className="land-mobile-link" onClick={() => setMobileMenuOpen(false)}>{l}</a>)}
            <button className="land-btn-primary land-mobile-cta" onClick={onLogin}>Get Started Free</button>
          </div>
        )}
      </nav>

      {/* ── Hero ── */}
      <section className="land-hero">
        <div className="land-hero-bg">
          <div className="land-hero-orb land-orb-1" />
          <div className="land-hero-orb land-orb-2" />
          <div className="land-hero-dots" />
        </div>
        <div className="land-hero-inner">
          <div className="land-hero-badge">AI-Powered Job Application Tracker</div>
          <h1 className="land-hero-title">
            Stop losing track.<br />
            Start <span className="land-hero-accent">landing offers.</span>
          </h1>
          <p className="land-hero-sub">
            ApplAI tracks every application, prepares you for every interview, and uses AI to help you choose the right opportunities — all in one place.
          </p>
          <div className="land-hero-actions">
            <button className="land-btn-hero-primary" onClick={onLogin}>
              Start for Free — No Credit Card
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
            <a href="#features" className="land-btn-hero-ghost">See what it does →</a>
          </div>
          <div className="land-hero-stats">
            <div className="land-stat"><span className="land-stat-num">6+</span><span className="land-stat-label">AI Features</span></div>
            <div className="land-stat-divider" />
            <div className="land-stat"><span className="land-stat-num">∞</span><span className="land-stat-label">Applications</span></div>
            <div className="land-stat-divider" />
            <div className="land-stat"><span className="land-stat-num">Free</span><span className="land-stat-label">To Start</span></div>
          </div>
        </div>

        {/* Hero visual — mini dashboard mockup */}
        <div className="land-hero-visual">
          <div className="land-mockup">
            <div className="land-mockup-bar">
              <span className="lmb-dot lmb-red"/><span className="lmb-dot lmb-yellow"/><span className="lmb-dot lmb-green"/>
              <span className="lmb-title">ApplAI — Applications</span>
            </div>
            <div className="land-mockup-body">
              {[
                { co: 'Nexora Corp', role: 'Software Engineer', status: 'Interview', color: '#6366F1', bg: '#EEF2FF' },
                { co: 'Meridian Bank', role: 'Data Analyst', status: 'Offer', color: '#15803D', bg: '#F0FDF4' },
                { co: 'SwiftCart', role: 'Backend Dev', status: 'Applied', color: '#1D4ED8', bg: '#EFF6FF' },
                { co: 'Stellar Tech', role: 'DevOps Engineer', status: 'Screening', color: '#B45309', bg: '#FFFBEB' },
              ].map((r, i) => (
                <div key={i} className="land-mockup-row" style={{ animationDelay: `${i * 0.12}s` }}>
                  <div className="lmr-avatar" style={{ background: r.bg, color: r.color }}>{r.co[0]}</div>
                  <div className="lmr-info">
                    <div className="lmr-co">{r.co}</div>
                    <div className="lmr-role">{r.role}</div>
                  </div>
                  <span className="lmr-status" style={{ background: r.bg, color: r.color }}>{r.status}</span>
                </div>
              ))}
              <div className="land-mockup-ai-card">
                <div className="lmac-label">AI Analysis — Nexora Corp</div>
                <div className="lmac-score">8.4 <span>/10</span></div>
                <div className="lmac-note">"Strong growth potential — clarify equity vesting"</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="land-section" id="features">
        <div className="land-section-inner">
          <div className="land-section-head">
            <div className="land-section-badge">Features</div>
            <h2 className="land-section-title">Everything your job search needs</h2>
            <p className="land-section-sub">From tracking to preparation to AI analysis — ApplAI covers the entire journey.</p>
          </div>
          <div className="land-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="land-feature-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="lfc-icon">{f.icon}</div>
                <h3 className="lfc-title">{f.title}</h3>
                <p className="lfc-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section className="land-section land-section-alt" id="how-it-works">
        <div className="land-section-inner">
          <div className="land-section-head">
            <div className="land-section-badge">How it Works</div>
            <h2 className="land-section-title">From first application to final offer</h2>
          </div>
          <div className="land-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="land-step">
                <div className="ls-num">{s.num}</div>
                <div className="ls-line" />
                <h3 className="ls-title">{s.title}</h3>
                <p className="ls-desc">{s.desc}</p>
                <span className="ls-badge">{s.badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="land-section">
        <div className="land-section-inner">
          <div className="land-section-head">
            <div className="land-section-badge">Stories</div>
            <h2 className="land-section-title">Real results, real people</h2>
          </div>
          <div className="land-testimonials">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="land-testimonial">
                <div className="lt-quote">"</div>
                <p className="lt-text">{t.quote}</p>
                <div className="lt-author">
                  <div className="lt-avatar">{t.name[0]}</div>
                  <div>
                    <div className="lt-name">{t.name}</div>
                    <div className="lt-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="land-section land-section-alt" id="pricing">
        <div className="land-section-inner">
          <div className="land-section-head">
            <div className="land-section-badge">Pricing</div>
            <h2 className="land-section-title">Simple, honest pricing</h2>
            <p className="land-section-sub">Start free. Upgrade to Pro when you need the full AI toolkit.</p>
            <div className="lpt-currency-toggle">
              <button className={`lpt-currency-btn ${currency === 'PHP' ? 'active' : ''}`} onClick={() => setCurrency('PHP')}>₱ PHP</button>
              <button className={`lpt-currency-btn ${currency === 'USD' ? 'active' : ''}`} onClick={() => setCurrency('USD')}>$ USD</button>
            </div>
          </div>
          {/* Pro billing plan cards */}
          <div className="lpt-billing-section">
            <p className="lpt-billing-intro">
              Start free forever. Upgrade to Pro for full AI access — all plans include identical features.
            </p>
            <div className="lpt-billing-grid">
              {/* Starter card */}
              <div className="lpt-billing-card lpt-billing-starter">
                <div className="lpt-bc-badge lpt-bc-badge-free">Free Forever</div>
                <div className="lpt-bc-name">Starter</div>
                <div className="lpt-bc-price">₱0</div>
                <div className="lpt-bc-desc">15 AI credits/month, full job tracking.</div>
                <ul className="lpt-bc-features">
                  <li>✓ Unlimited job tracking</li>
                  <li>✓ Kanban &amp; table views</li>
                  <li>✓ Calendar &amp; events</li>
                  <li>✓ 15 AI credits/month</li>
                </ul>
                <button className="lpt-cta lpt-cta-free lpt-bc-cta" onClick={onLogin}>Get Started Free</button>
              </div>
              {/* Pro billing options */}
              {PRO_BILLING.map(p => (
                <div key={p.id} className={`lpt-billing-card lpt-billing-pro ${p.id === '6month' ? 'lpt-billing-best' : ''}`}>
                  {p.badge
                    ? <div className={`lpt-bc-badge ${p.id === '6month' ? 'lpt-bc-badge-gold' : ''}`}>{p.badge}</div>
                    : <div className="lpt-bc-badge lpt-bc-badge-invisible">·</div>
                  }
                  <div className="lpt-bc-name lpt-bc-name-pro">Pro — {p.label}</div>
                  <div className="lpt-bc-price lpt-bc-price-pro">
                    {currency === 'PHP' ? `₱${p.totalPHP}` : `$${p.totalUSD}`}
                  </div>
                  {p.perMonthPHP !== p.totalPHP && (
                    <div className="lpt-bc-per-month">
                      {currency === 'PHP' ? `₱${p.perMonthPHP}` : `$${p.perMonthUSD}`}/mo
                    </div>
                  )}
                  {p.savingsPHP && (
                    <div className="lpt-bc-savings">
                      Save {currency === 'PHP' ? `₱${p.savingsPHP}` : `$${p.savingsUSD}`} vs monthly
                    </div>
                  )}
                  <div className="lpt-bc-desc">{p.desc}</div>
                  <ul className="lpt-bc-features lpt-bc-features-pro">
                    <li>✓ 300 AI credits/month</li>
                    <li>✓ PDF job parsing</li>
                    <li>✓ AI job comparison</li>
                    <li>✓ Email reminders</li>
                    <li>✓ Resume upload</li>
                  </ul>
                  <button className="lpt-cta lpt-cta-pro lpt-bc-cta" onClick={onLogin}>
                    {p.id === '6month' ? 'Get Best Value' : 'Upgrade to Pro'}
                  </button>
                </div>
              ))}
            </div>
            <p className="lpt-billing-note">
              Accepts GCash, QR PH, and debit/credit cards via PayMongo. 
            </p>
          </div>

          {/* Feature comparison table */}
          <div className="lpt-compare-heading">Full feature comparison</div>
          <div className="land-pricing-table-wrap">
            <table className="land-pricing-table">
              <thead>
                <tr>
                  <th className="lpt-col-feature" />
                  <th className="lpt-col-plan">
                    <div className="lpt-pro-badge" style={{ visibility: 'hidden', pointerEvents: 'none' }}>Most Popular</div>
                    <div className="lpt-plan-name">Starter</div>
                    <div className="lpt-plan-price">₱0 <span className="lpt-plan-period">/ forever</span></div>
                    <button className="lpt-cta lpt-cta-free" onClick={onLogin}>Get Started Free</button>
                  </th>
                  <th className="lpt-col-plan lpt-col-pro">
                    <div className="lpt-pro-badge">Most Popular</div>
                    <div className="lpt-plan-name lpt-plan-name-pro">Pro</div>
                    <div className="lpt-plan-price lpt-plan-price-pro">
                      {currency === 'PHP' ? '₱199' : '$3.50'} <span className="lpt-plan-period">/ month</span>
                    </div>
                    <button className="lpt-cta lpt-cta-pro" onClick={onLogin}>Upgrade to Pro</button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRICING_ROWS.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'lpt-row-alt' : ''}>
                    <td className="lpt-td-label">{row.label}</td>
                    <td className="lpt-td-val">
                      {row.starter === true ? <span className="lpt-check">✓</span>
                        : row.starter === false ? <span className="lpt-x">—</span>
                        : <span className="lpt-text">{row.starter}</span>}
                    </td>
                    <td className="lpt-td-val lpt-td-pro">
                      {row.pro === true ? <span className="lpt-check lpt-check-pro">✓</span>
                        : row.pro === false ? <span className="lpt-x">—</span>
                        : <span className="lpt-text lpt-text-pro">{row.pro}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="land-section" id="faq">
        <div className="land-section-inner land-section-inner-narrow">
          <div className="land-section-head">
            <div className="land-section-badge">FAQ</div>
            <h2 className="land-section-title">Common questions</h2>
          </div>
          <div className="land-faq">
            {FAQS.map((item, i) => (
              <div key={i} className={`land-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="land-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{item.q}</span>
                  <ChevronDown size={16} className="land-faq-chevron" />
                </button>
                <div className="land-faq-a">
                  <p>{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="land-cta-banner">
        <div className="land-cta-inner">
          <h2 className="land-cta-title">Ready to apply smarter?</h2>
          <p className="land-cta-sub">Join job seekers who track, analyze, and prepare with AI — for free.</p>
          <button className="land-btn-hero-primary" onClick={onLogin}>
            Create Your Free Account
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="land-footer">
        <div className="land-footer-inner">
          <div className="land-footer-col land-footer-brand-col">
            <div className="land-footer-brand">
              <img src={applaiLogo} alt="ApplAI" className="land-footer-logo" />
              <span className="land-footer-brand-name">Appl<span className="land-ai">AI</span></span>
            </div>
            <p className="land-footer-tagline">AI-powered job application tracker for the modern job seeker.</p>
            <p className="land-footer-copy">© {new Date().getFullYear()} ApplAI. All rights reserved.</p>
          </div>
          <div className="land-footer-col">
            <div className="land-footer-col-title">Product</div>
            <a href="#features" className="land-footer-link">Features</a>
            <a href="#how-it-works" className="land-footer-link">How it Works</a>
            <a href="#pricing" className="land-footer-link">Pricing</a>
            <a href="#faq" className="land-footer-link">FAQ</a>
          </div>
          <div className="land-footer-col">
            <div className="land-footer-col-title">Support</div>
            <button className="land-footer-link" onClick={() => setShowContact(true)}>Contact Us</button>
            <button className="land-footer-link" onClick={() => setShowContact(true)}>Report an Issue</button>
            <a href="#faq" className="land-footer-link">Help &amp; FAQ</a>
          </div>
          <div className="land-footer-col">
            <div className="land-footer-col-title">Legal</div>
            <button className="land-footer-link" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
            <button className="land-footer-link" onClick={() => setShowTerms(true)}>Terms of Service</button>
          </div>
        </div>
      </footer>

      {/* ── Modals ── */}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}

      {showPrivacy && (
        <div className="land-legal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowPrivacy(false); }}>
          <div className="land-legal-panel">
            <div className="land-legal-header">
              <h2>Privacy Policy</h2>
              <button className="land-legal-close" onClick={() => setShowPrivacy(false)}>✕</button>
            </div>
            <div className="land-legal-body">
              <p><strong>Last updated:</strong> {new Date().getFullYear()}</p>
              <h3>What we collect</h3>
              <p>We collect your name, email address, and the job application data you enter. If you sign in with Google, we receive your name, email, and profile picture from Google.</p>
              <h3>How we use it</h3>
              <p>Your data is used solely to provide the ApplAI service — displaying your applications, generating AI analysis, and sending calendar reminders you request. We do not sell, share, or use your data for advertising.</p>
              <h3>AI processing</h3>
              <p>Job details and profile information you provide are sent to Google Gemini to generate analysis and interview preparation content. Gemini processes this data according to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's privacy policy</a>.</p>
              <h3>Data storage</h3>
              <p>Your data is stored in a MongoDB database. Passwords are hashed with bcrypt and are never stored in plain text. JWT authentication tokens are stored only in your browser.</p>
              <h3>Data deletion</h3>
              <p>You can delete your account and all associated data at any time by contacting us via the Contact form.</p>
              <h3>Contact</h3>
              <p>Questions about privacy? <button className="land-legal-inline-link" onClick={() => { setShowPrivacy(false); setShowContact(true); }}>Contact us</button>.</p>
            </div>
            <div className="land-legal-footer">
              <button className="land-legal-done" onClick={() => setShowPrivacy(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showTerms && (
        <div className="land-legal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowTerms(false); }}>
          <div className="land-legal-panel">
            <div className="land-legal-header">
              <h2>Terms of Service</h2>
              <button className="land-legal-close" onClick={() => setShowTerms(false)}>✕</button>
            </div>
            <div className="land-legal-body">
              <p><strong>Last updated:</strong> {new Date().getFullYear()}</p>
              <h3>Acceptance</h3>
              <p>By using ApplAI, you agree to these terms. If you do not agree, please do not use the service.</p>
              <h3>Use of the service</h3>
              <p>ApplAI is provided for personal, non-commercial use to assist with individual job searching. You may not use it to scrape data at scale, impersonate others, or violate any applicable laws.</p>
              <h3>AI-generated content</h3>
              <p>Analysis, preparation guides, and other AI-generated content are provided for informational purposes only. We make no guarantees about accuracy or outcomes. Always exercise your own judgment.</p>
              <h3>Account responsibility</h3>
              <p>You are responsible for keeping your account credentials secure. You are responsible for all activity that occurs under your account.</p>
              <h3>Service availability</h3>
              <p>We provide ApplAI "as is" without warranty of any kind. We may modify or discontinue the service at any time without notice.</p>
              <h3>Limitation of liability</h3>
              <p>ApplAI is not liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
              <h3>Contact</h3>
              <p>Questions about these terms? <button className="land-legal-inline-link" onClick={() => { setShowTerms(false); setShowContact(true); }}>Contact us</button>.</p>
            </div>
            <div className="land-legal-footer">
              <button className="land-legal-done" onClick={() => setShowTerms(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
